import Vue from 'vue'
import VueRouter from 'vue-router'

// Routes
import { canNavigate } from '@/libs/acl/routeProtection'
import { isUserLoggedIn, getHomeRouteForLoggedInUser } from '@/auth/utils'
import store from '@/store'
import * as types from '@/modules/account/store/types'
import * as franchiseManualTypes from '@/modules/franchise_manual/store/types'
import { isPendingAppointment } from '@/modules/account/store/report_lock'
import { initApi, resetReportLockRedirect } from '@/helpers/api'
import { checkUpdatesOnceADay } from '@/helpers/system_updates'
import {
  REPORT_HUB_ROUTE,
  REPORT_ROUTE,
  isReportHubReturnTarget,
  reportHubLocation,
} from '@/constants/appointment_report_v2'
import account from './routes/account'
import contract from './routes/contract'
import kpi from './routes/kpi'
import consultant from './routes/consultant'
import miscellaneous from './routes/miscellaneous'
import report from './routes/report'
import notification from './routes/notification'
import customer from './routes/customer'
import appointment from './routes/appointment'
import system from './routes/system'
import commission from './routes/commission'
import withdrawal from './routes/withdrawal'
import financialAdjustment from './routes/financial_adjustment'
import financialManagement from './routes/financial_management'
import franchises from './routes/franchises'
import dashboard from './routes/dashboard'
import task from './routes/task'
import nps from './routes/nps'
import accountingManagement from './routes/accounting_management'
import financialPlanning from './routes/financial_planning'
import planImplementation from './routes/plan_implementation'
import aiPreInterview from './routes/ai_pre_interview'
import investments from './routes/investments'
import kpiFounders from './routes/kpi_founders'
import biReports from './routes/bi_reports'
import copilot from './routes/copilot'
import customerChat from './routes/customer_chat'

initApi()

Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    { path: '/', redirect: { name: 'account-login' } },
    ...account,
    ...contract,
    ...kpi,
    ...investments,
    ...consultant,
    ...report,
    ...miscellaneous,
    ...notification,
    ...customer,
    ...appointment,
    ...system,
    ...commission,
    ...withdrawal,
    ...financialAdjustment,
    ...financialManagement,
    ...franchises,
    ...dashboard,
    ...task,
    ...nps,
    ...accountingManagement,
    ...financialPlanning,
    ...planImplementation,
    ...aiPreInterview,
    ...kpiFounders,
    ...biReports,
    ...copilot,
    ...customerChat,
    {
      path: '*',
      redirect: 'error-404',
    },
  ],
})

const passTroughRoutes = {
  standardRoutes: ['account-token-auth', 'misc-not-authorized', 'auth-login'],
  contractRoutes: ['contract-save'],
  companyRoutes: [
    'consultant-company-info-save',
    'lead-profile',
    'customer-profile',
    'contract-details-summary',
    'ticket-details-summary',
  ],
}

// Com report pendente o PN só chega à central, ao report de um compromisso pendente e às telas de
// autenticação/erro de acesso. Todo o resto vai para a central (o servidor recusa as mesmas rotas com 423).
// Fora da lista: `appify-auth` / `appify-auth-commissions` — a API responde 423 em `users/link_appify` para o PN
// travado, então a tela abriria só para falhar.
const REPORT_LOCK_BLOCKED_ACCOUNT_ROUTES = ['appify-auth', 'appify-auth-commissions']
const REPORT_LOCK_PASS_ROUTES = [
  REPORT_HUB_ROUTE,
  ...passTroughRoutes.standardRoutes,
  ...account.map(route => route.name).filter(name => name && !REPORT_LOCK_BLOCKED_ACCOUNT_ROUTES.includes(name)),
]

const isAllowedWhileReportLocked = (to, lock) => {
  if (REPORT_LOCK_PASS_ROUTES.includes(to.name)) return true

  return to.name === REPORT_ROUTE && isPendingAppointment(lock, to.params.id)
}

// Checagens em segundo plano (redirecionam sozinhas quando acham pendência). Só rodam sem a trava de
// report: ela tem precedência e a central não pode ser trocada por outra tela obrigatória.
const runBackgroundPendencyChecks = to => {
  // What's New cede a prioridade aos fluxos obrigatórios (contrato pendente chega com params.blocked).
  if (!to.params.blocked) {
    store.dispatch('whats-new/CHECK_CURRENT_WHATS_NEW')
  }
  if (!passTroughRoutes.standardRoutes.includes(to.name)) {
    store.dispatch(types.CHECK_PN_ONBOARDING_STATUS)
  }
  if (!passTroughRoutes.companyRoutes.includes(to.name)) {
    store.dispatch(types.CHECK_COMPANY_PENDENCIES)
  }
  if (!passTroughRoutes.contractRoutes.includes(to.name)) {
    store.dispatch(types.CHECK_CONTRACT_REPORT_PENDENCIES)
  }
}

router.beforeEach(async (to, from, next) => {
  const isLoggedIn = isUserLoggedIn()
  if (isLoggedIn) {
    checkUpdatesOnceADay(store)
    store.dispatch(types.CHECK_SYSTEM_VERSION_NOTIFICATIONS)
    // Manuais do Franqueado: o bloqueio deles vive só no front (o modal global lê este estado). Roda também
    // com a trava de report — a API libera `franchise_manual_cycles/my_cycle*` para o PN travado, e as duas
    // travas não podem se prender. Fora de standardRoutes para não disputar com PN_ACCESS_BLOCKED, que já
    // redireciona para misc-not-authorized.
    if (!passTroughRoutes.standardRoutes.includes(to.name)) {
      store.dispatch(franchiseManualTypes.GET_MY_FRANCHISE_MANUAL_CYCLE).catch(() => {})
    }
    if (to.name === 'account-login') {
      return next({ name: 'dashboard' })
    }
  }
  if (!to.meta.public && !isLoggedIn) {
    return next({ name: 'account-login' })
  }
  if (isLoggedIn) {
    // A decisão ESPERA a checagem (cache de 30 s no store): a tela pedida nunca abre para depois
    // redirecionar. Para quem não é PN a action resolve na hora, sem requisição. Ela não rejeita, mas
    // um guard async que lança deixaria a navegação pendurada — por isso o try/catch.
    let lock = null
    try {
      lock = await store.dispatch(types.REFRESH_REPORT_LOCK)
    } catch {
      lock = store.getters[types.REPORT_LOCK]
    }
    if (lock && lock.locked) {
      if (!isAllowedWhileReportLocked(to, lock)) {
        // Já na central e a tela pedida não pode ser guardada (ex.: o "Cadastrar contrato" da liberação — `contract-save`
        // com o compromisso nos params — clicado quando uma pendência nova travou de novo): fica nela, com o `?voltar=`
        // que ela já tem. Trocar agora faria o "Continuar" abrir um contrato em branco (sem o compromisso).
        if (from && from.name === REPORT_HUB_ROUTE && !isReportHubReturnTarget(to)) return next(false)
        // Guarda a tela pedida (`?voltar=`): "Continuar", quando o acesso for liberado, volta para ela.
        return next(reportHubLocation(to))
      }
    } else {
      runBackgroundPendencyChecks(to)
    }
  }
  if (!canNavigate(to) && !to.meta.public) {
    // If logged in => not authorized
    return next({
      name: 'misc-not-authorized',
      params: { errorCode: 'NO_PERMISSION' },
    })
  }
  // Redirect if logged in
  if (to.meta.redirectIfLoggedIn && isLoggedIn) {
    return next(getHomeRouteForLoggedInUser())
  }

  return next()
})

// ? For splash screen
// Remove afterEach hook if you are not using splash screen
router.afterEach(() => {
  // Uma navegação concluída libera um novo redirecionamento do 423 da trava (helpers/api.js).
  resetReportLockRedirect()
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

export default router
